import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ArrowDownSIcon } from '@fmlx/icons';
import {
  FmlxButton,
  FmlxCheckBox,
  FmlxChip,
  FmlxIcon,
  FmlxPopover,
  FmlxTextBox,
  FmlxToggleButton,
} from 'fmlx-common-ui';
import { LogicalOperator } from 'constants/types';
import { CHIP_GAP, countFittingChips, getVisibleChipCount } from './utils';

export type OptionId = string | number;

const LOGIC_ITEMS = [
  { id: 0, text: 'Or', icon: null as React.ReactNode, disabled: false },
  { id: 1, text: 'And', icon: null as React.ReactNode, disabled: false },
];

export interface RMMultiSelectSearchDropdownProps<T, TId extends OptionId = OptionId> {
  id?: string;

  options: T[];

  selectedIds: TId[];

  onSelectionChange: (nextSelectedIds: TId[]) => void;

  filterLogic?: LogicalOperator;

  onFilterLogicChange?: (next: LogicalOperator) => void;

  getOptionId: (option: T) => TId;

  getOptionLabel: (option: T) => string;

  getOptionGroup?: (option: T) => string | undefined | null;

  getOptionDisabled?: (option: T) => boolean;

  placeholder?: string;

  searchPlaceholder?: string;

  enableSearch?: boolean;

  enableLogic?: boolean;

  enableSelectAll?: boolean;

  selectAllLabel?: string;

  disabled?: boolean;

  className?: string;

  listMaxHeight?: number;

  getTriggerLabel?: (selectedIds: TId[]) => string;

  /** Caps how many chips to show. Omit to fill available width. */
  maxVisibleChips?: number;
}

function RMMultiSelectSearchDropdown<T, TId extends OptionId = OptionId>(
  props: RMMultiSelectSearchDropdownProps<T, TId>,
) {
  const {
    id: rootId = 'rm-multi-select-search',
    options,
    selectedIds,
    onSelectionChange,
    filterLogic = LogicalOperator.Or,
    onFilterLogicChange = () => {},
    getOptionId,
    getOptionLabel,
    getOptionGroup,
    getOptionDisabled,
    placeholder = 'Select',
    searchPlaceholder = 'Search all',
    enableSearch = true,
    enableLogic = true,
    enableSelectAll = false,
    selectAllLabel = 'Select All',
    disabled = false,
    className = '',
    listMaxHeight = 220,
    getTriggerLabel,
    maxVisibleChips,
  } = props;

  const anchorRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [fitCount, setFitCount] = useState(selectedIds.length);

  const normalizeId = (id: OptionId) => String(id);

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const selectedInOrder = useMemo(() => {
    const map = new Map<string, T>(
      options.map((option) => [normalizeId(getOptionId(option)), option]),
    );

    return selectedIds.map((id) => map.get(normalizeId(id))).filter(Boolean) as T[];
  }, [options, selectedIds, getOptionId]);

  const visibleCount = getVisibleChipCount(
    selectedInOrder.length,
    fitCount,
    maxVisibleChips,
  );

  const visibleSelected = selectedInOrder.slice(0, visibleCount);
  const overflowExtra = selectedInOrder.length - visibleCount;

  useLayoutEffect(() => {
    if (maxVisibleChips != null || getTriggerLabel || selectedInOrder.length === 0) {
      setFitCount(selectedInOrder.length);
      return undefined;
    }

    const container = chipsRef.current;
    if (!container) {
      return undefined;
    }

    const update = () => setFitCount(countFittingChips(container));

    update();

    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, [maxVisibleChips, getTriggerLabel, selectedInOrder]);

  const filtered = useMemo(() => {
    if (!enableSearch) {
      return options;
    }

    const q = search.trim().toLowerCase();

    if (!q) {
      return options;
    }

    return options.filter((option) => getOptionLabel(option).toLowerCase().includes(q));
  }, [options, search, getOptionLabel, enableSearch]);

  const groupedFiltered = useMemo(() => {
    if (!getOptionGroup) {
      return [{ groupLabel: '', options: filtered }];
    }

    const groups = new Map<string, T[]>();

    filtered.forEach((option) => {
      const groupLabel = getOptionGroup(option) ?? '';
      const existingGroup = groups.get(groupLabel);

      if (existingGroup) {
        existingGroup.push(option);
      } else {
        groups.set(groupLabel, [option]);
      }
    });

    return Array.from(groups.entries()).map(([groupLabel, options]) => ({
      groupLabel,
      options,
    }));
  }, [filtered, getOptionGroup]);

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback(() => {
    if (!disabled) {
      setOpen((o) => !o);
    }
  }, [disabled]);

  const toggleOption = (option: T) => {
    const optionId = getOptionId(option);
    const optionKey = normalizeId(optionId);

    const optionDisabled = getOptionDisabled?.(option);

    if (optionDisabled) {
      return;
    }

    const exists = selectedIds.some((id) => normalizeId(id) === optionKey);

    const nextSelectedIds = exists
      ? selectedIds.filter((id) => normalizeId(id) !== optionKey)
      : [...selectedIds, optionId];

    onSelectionChange(nextSelectedIds);
  };

  const selectedKeySet = useMemo(
    () => new Set(selectedIds.map(normalizeId)),
    [selectedIds],
  );

  const selectableFiltered = useMemo(
    () => filtered.filter((option) => !getOptionDisabled?.(option)),
    [filtered, getOptionDisabled],
  );

  const filteredSelectedCount = useMemo(
    () =>
      selectableFiltered.reduce(
        (count, option) =>
          selectedKeySet.has(normalizeId(getOptionId(option))) ? count + 1 : count,
        0,
      ),
    [selectableFiltered, selectedKeySet, getOptionId],
  );

  const allFilteredSelected =
    selectableFiltered.length > 0 && filteredSelectedCount === selectableFiltered.length;

  const someFilteredSelected = filteredSelectedCount > 0 && !allFilteredSelected;

  const onSelectAllToggle = () => {
    if (allFilteredSelected) {
      const filteredKeys = new Set(
        selectableFiltered.map((option) => normalizeId(getOptionId(option))),
      );

      onSelectionChange(selectedIds.filter((id) => !filteredKeys.has(normalizeId(id))));

      return;
    }

    const toAdd = selectableFiltered
      .map((option) => getOptionId(option))
      .filter((id) => !selectedKeySet.has(normalizeId(id)));

    onSelectionChange([...selectedIds, ...toAdd]);
  };

  const onLogicChange = (elementId: number | null) => {
    if (elementId === 0) {
      onFilterLogicChange(LogicalOperator.Or);
    } else if (elementId === 1) {
      onFilterLogicChange(LogicalOperator.And);
    }
  };

  const popperModifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: [0, 4] } },
      {
        name: 'sameWidth',
        enabled: true,
        phase: 'beforeWrite',
        requires: ['computeStyles'],
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        effect: ({ state }) => {
          const { reference, popper } = state.elements;

          popper.style.width = `${reference.getBoundingClientRect().width}px`;

          return () => {
            popper.style.width = '';
          };
        },
      },
    ],
    [],
  );

  const popoverProps = {
    isOverlay: false,
    anchorReference: 'anchorEl',
    anchorEl: open ? anchorRef.current : null,
    open,
    placement: 'bottom-start',
    onClose: close,
    disablePortal: false,
    popperModifiers,
    id: `${rootId}-popover`,
  } as const;

  return (
    <div
      id={rootId}
      className={`relative w-full text-[13px] text-[#313131] ${className}`.trim()}
    >
      <div
        ref={anchorRef}
        className={`flex min-h-8 w-full items-stretch rounded-sm border border-[#bdbdbd] bg-white ${
          disabled ? 'pointer-events-none opacity-[0.55]' : ''
        }`}
      >
        <div
          ref={chipsRef}
          className="relative flex min-w-0 flex-1 cursor-pointer items-center overflow-hidden px-2 py-1"
          style={{ gap: CHIP_GAP }}
          onClick={toggle}
        >
          {getTriggerLabel ? (
            <span>{getTriggerLabel(selectedIds)}</span>
          ) : selectedIds.length === 0 ? (
            <span className="text-[#757575]">{placeholder}</span>
          ) : (
            <>
              {/* Hidden row used only to measure chip widths for auto-fit */}
              {maxVisibleChips == null && (
                <div
                  aria-hidden
                  className="pointer-events-none invisible absolute flex"
                  style={{ gap: CHIP_GAP }}
                >
                  {selectedInOrder.map((option) => (
                    <div
                      key={`m-${normalizeId(getOptionId(option))}`}
                      data-chip
                      className="shrink-0"
                    >
                      <FmlxChip
                        id={`${rootId}-m-${normalizeId(getOptionId(option))}`}
                        size="sm"
                        variant="filled"
                        type="default"
                        label={getOptionLabel(option)}
                        rightIcon={
                          <FmlxIcon
                            name="CloseOutline"
                            customSize={10}
                            customColor="#fff"
                          />
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              {visibleSelected.map((option) => {
                const optionId = normalizeId(getOptionId(option));

                return (
                  <div key={optionId} className="shrink-0">
                    <FmlxChip
                      id={`${rootId}-chip-${optionId}`}
                      size="sm"
                      variant="filled"
                      type="default"
                      readOnly={false}
                      label={getOptionLabel(option)}
                      rightIcon={
                        <FmlxIcon
                          id={`${rootId}-chip-${optionId}-x`}
                          name="CloseOutline"
                          customSize={10}
                          customColor="#fff"
                        />
                      }
                      onClick={() => {}}
                      onRightIconClick={() => {
                        onSelectionChange(
                          selectedIds.filter((x) => normalizeId(x) !== optionId),
                        );
                      }}
                    />
                  </div>
                );
              })}

              {overflowExtra > 0 && <span className="shrink-0">+{overflowExtra}</span>}
            </>
          )}
        </div>

        <FmlxButton
          id={`${rootId}-caret`}
          type={FmlxButton.Type.BASIC}
          variant={FmlxButton.Variant.PLAIN}
          size={FmlxButton.Size.SMALL}
          onlyIcon
          disabled={disabled}
          withTooltip={false}
          icon={
            <ArrowDownSIcon id={`${rootId}-caret-icon`} size="xs" customColor="#474747" />
          }
          onClick={toggle}
        />
      </div>

      {/* @ts-expect-error */}
      <FmlxPopover {...popoverProps}>
        {(enableSearch || enableLogic) && (
          <div className="flex flex-col gap-2 border-b border-[#e0e0e0] pb-2">
            <div className="flex items-stretch gap-2">
              {enableSearch && (
                <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-sm border border-[#bdbdbd] px-2">
                  <FmlxIcon
                    id={`${rootId}-search-ic`}
                    name="Search"
                    size={FmlxIcon.Size.SMALL}
                    customColor="#757575"
                  />

                  <div className="min-w-0 flex-1">
                    <FmlxTextBox
                      id={`${rootId}-search`}
                      size={FmlxTextBox.Size.SMALL}
                      variant={FmlxTextBox.Variant.OUTLINE}
                      placeholder={searchPlaceholder}
                      value={search}
                      decoration={FmlxTextBox.Decoration.NONE}
                      onChange={({ value }) => setSearch(value)}
                    />
                  </div>
                </div>
              )}

              {enableLogic && (
                <FmlxToggleButton
                  id={`${rootId}-logic-toggle`}
                  variant="text"
                  size="sm"
                  items={LOGIC_ITEMS}
                  value={filterLogic === LogicalOperator.Or ? 0 : 1}
                  multipleSelection={false}
                  onChange={onLogicChange as () => void}
                />
              )}
            </div>
          </div>
        )}

        <div
          role="listbox"
          className="max-h-[220px] overflow-y-auto py-1"
          style={{ maxHeight: listMaxHeight }}
        >
          {enableSelectAll && selectableFiltered.length > 0 && (
            <div className="px-3 py-1 hover:bg-[rgb(128_180_250/15%)]">
              <FmlxCheckBox
                id={`${rootId}-cb-select-all`}
                size={FmlxCheckBox.Size.SMALL}
                label={selectAllLabel}
                checked={allFilteredSelected}
                indeterminate={someFilteredSelected}
                onChange={onSelectAllToggle}
              />
            </div>
          )}

          {groupedFiltered.map(({ groupLabel, options: groupOptions }) => (
            <React.Fragment key={`group-${groupLabel || 'ungrouped'}`}>
              {groupLabel ? (
                <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[#757575]">
                  {groupLabel}
                </div>
              ) : null}
              {groupOptions.map((option) => {
                const optionId = getOptionId(option);

                const label = getOptionLabel(option);

                const optionDisabled = getOptionDisabled?.(option);

                const checked = selectedIds.some(
                  (id) => normalizeId(id) === normalizeId(optionId),
                );

                return (
                  <div
                    key={normalizeId(optionId)}
                    className={`px-3 py-1 hover:bg-[rgb(128_180_250/15%)] ${
                      optionDisabled ? 'pointer-events-none opacity-45' : ''
                    }`}
                  >
                    <FmlxCheckBox
                      id={`${rootId}-cb-${normalizeId(optionId)}`}
                      size={FmlxCheckBox.Size.SMALL}
                      label={label}
                      checked={checked}
                      disabled={optionDisabled}
                      onChange={() => toggleOption(option)}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </FmlxPopover>
    </div>
  );
}

export default RMMultiSelectSearchDropdown;
